// =============================================
// Music entitlements — per-plan song/storage caps + upload gate
// =============================================
// Mirrors src/lib/entitlements.ts (Client's Accounts), but for the Music service.
//
// Tier model (decided 2026-06-15, see memory akt-music-tiers):
//   Free (no ads)      → 50 songs        ── the plan's baseline cap
//   Free (ad-supported)→ ~500 songs      ── ads buy a BIGGER cap, not "unlimited"
//   Paid               → large / unlimited (no ads)
//
// We HOST uploads (permanent storage cost), so caps are enforced on BOTH count
// AND megabytes (50 FLACs ≫ 50 MP3s). The plan columns (max_music_songs /
// max_music_mb / can_upload_video, migration 074) hold the NO-ADS baseline; this
// module applies the ad-supported bonus for free users (the app always serves ads
// to the free tier) and treats Enterprise as unlimited.
// =============================================
import { getTableData } from '../features/db-viewer/data/dataAccess';

// Ad-supported free users get this lifted cap instead of the no-ads baseline.
// (The free experience in this app is ad-supported; a true ad-free 50-song mode
// would be a future toggle that returns the baseline instead.)
export const FREE_ADS_SONG_CAP = 500;
export const FREE_ADS_MB_CAP = 5000;

function sessionUserId(): number | null {
  try {
    const raw = localStorage.getItem('akt_session_user');
    if (!raw) return null;
    const id = JSON.parse(raw).id;
    return id != null ? Number(id) : null;
  } catch { return null; }
}

/** Resolve the session (or given) user's plan row from users.plan_id. */
function planRowFor(userId: number | null): any | null {
  if (userId == null) return null;
  // eslint-disable-next-line eqeqeq
  const user = getTableData('users').find((u: any) => u.user_id == userId);
  if (!user || user.plan_id == null) return null;
  // eslint-disable-next-line eqeqeq
  return getTableData('site_service_plans').find((p: any) => p.plan_id == user.plan_id) || null;
}

export interface MusicEntitlement {
  planCode: string;        // 'FREE' | 'BASIC' | 'PRO' | 'ENTERPRISE' | '' (unknown)
  unlimited: boolean;      // Enterprise — caps not enforced
  adSupported: boolean;    // true for the free tier (ads lift the cap)
  maxSongs: number;        // effective cap (Infinity when unlimited)
  maxMb: number;           // effective cap (Infinity when unlimited)
  canUploadVideo: boolean;
  baseSongs: number;       // the no-ads baseline (for "50 free, 500 with ads" copy)
  baseMb: number;
}

/**
 * Effective music entitlement for a user. Fail-open to the free ad-supported cap
 * when the plan can't be resolved (localStorage / pre-058 users with no plan_id),
 * so the feature stays usable rather than locking to 0.
 */
export function getMusicEntitlement(userId: number | null = sessionUserId()): MusicEntitlement {
  const plan = planRowFor(userId);
  const code = (plan?.plan_code || 'FREE') as string;

  if (code === 'ENTERPRISE') {
    return {
      planCode: code, unlimited: true, adSupported: false,
      maxSongs: Infinity, maxMb: Infinity, canUploadVideo: true,
      baseSongs: Infinity, baseMb: Infinity,
    };
  }

  const baseSongs = Number(plan?.max_music_songs ?? 50);
  const baseMb = Number(plan?.max_music_mb ?? 500);
  const canUploadVideo = Number(plan?.can_upload_video ?? 0) === 1;

  if (code === 'FREE') {
    // Ad-supported: the lifted cap, never below the baseline.
    return {
      planCode: code, unlimited: false, adSupported: true,
      maxSongs: Math.max(baseSongs, FREE_ADS_SONG_CAP),
      maxMb: Math.max(baseMb, FREE_ADS_MB_CAP),
      canUploadVideo,
      baseSongs, baseMb,
    };
  }

  // Paid tiers below Enterprise — no ads, plan caps as-is.
  return {
    planCode: code, unlimited: false, adSupported: false,
    maxSongs: baseSongs, maxMb: baseMb, canUploadVideo,
    baseSongs, baseMb,
  };
}

// ---- Usage counts (active, non-deleted owned tracks) ----
function ownedTrackIds(userId: number): Set<number> {
  const ids = new Set<number>();
  for (const o of getTableData('mus_user_owned')) {
    // eslint-disable-next-line eqeqeq
    if (o.user_id == userId && o.is_deleted != 1 && o.is_active != 0) ids.add(Number(o.track_id));
  }
  return ids;
}

export function countOwnedSongs(userId: number | null = sessionUserId()): number {
  if (userId == null) return 0;
  return ownedTrackIds(userId).size;
}

export function sumOwnedBytes(userId: number | null = sessionUserId()): number {
  if (userId == null) return 0;
  const ids = ownedTrackIds(userId);
  let bytes = 0;
  for (const t of getTableData('mus_tracks_all')) {
    if (ids.has(Number(t.track_id)) && t.is_deleted != 1) bytes += Number(t.file_size_bytes) || 0;
  }
  return bytes;
}

export const MB = 1024 * 1024;

export interface MusicGateResult {
  allowed: boolean;
  reason: string;
  songLimit: number;
  songCurrent: number;
  mbLimit: number;
  mbCurrent: number;
}

/**
 * Can this user upload a track of `addBytes` (and is it allowed to be video)?
 * Enforces song count AND storage MB, plus the no-video-for-free rule.
 */
export function canUploadTrack(
  addBytes: number,
  isVideo: boolean,
  userId: number | null = sessionUserId(),
): MusicGateResult {
  const ent = getMusicEntitlement(userId);
  const songCurrent = countOwnedSongs(userId);
  const mbCurrent = sumOwnedBytes(userId) / MB;
  const addMb = addBytes / MB;
  const base: Omit<MusicGateResult, 'allowed' | 'reason'> = {
    songLimit: ent.maxSongs, songCurrent,
    mbLimit: ent.maxMb, mbCurrent,
  };

  if (isVideo && !ent.canUploadVideo) {
    return { ...base, allowed: false, reason: `Video songs need a higher plan — your ${ent.planCode || 'Free'} plan is audio-only.` };
  }
  if (!ent.unlimited && songCurrent + 1 > ent.maxSongs) {
    return { ...base, allowed: false, reason: `You've reached your ${ent.maxSongs}-song limit. ${ent.adSupported ? 'Upgrade for more.' : 'Upgrade your plan to add more.'}` };
  }
  if (!ent.unlimited && mbCurrent + addMb > ent.maxMb) {
    return { ...base, allowed: false, reason: `This would exceed your ${ent.maxMb} MB storage limit (${mbCurrent.toFixed(0)} MB used).` };
  }
  return { ...base, allowed: true, reason: '' };
}
